import {
  Box as MantineBox,
  FileInput as MantineFileInput,
  Input as MantineInput,
  NativeSelect as MantineNativeSelect,
  NumberInput as MantineNumberInput,
  Textarea as MantineTextarea,
  TextInput as MantineTextInput,
  UnstyledButton as MantineUnstyledButton,
} from "@mantine/core";
import { useMergedRef, useOs } from "@mantine/hooks";
import classNames from "classnames";
import * as React from "react";

import type { ElementProps, SizeValue, StyleProps } from "../primitives/style-props";

import {
  changeNumberInputState,
  defaultNumberInputFormatter,
  defaultNumberInputParser,
  formatNumberInputValue,
  getNumberInputKeyStep,
  getNumberInputMode,
  isComposingNumberInputChange,
  type NumberInputState,
  type NumberInputTransition,
  type NumberInputValue,
  stepNumberInputState,
  syncNumberInputState,
} from "./number-input-value";

export type { NumberInputValue } from "./number-input-value";

/**
 * The label/feedback surface shared by every field control. Matches what `form.getInputProps()`
 * feeds (`value`, `onChange`, `error`) plus the wrapper props the call sites use — the
 * `@mantine/form` carve-out (see README) means these must keep accepting the hook's output with
 * no change to any form.
 */
interface FieldBaseProps {
  label?: React.ReactNode;
  description?: React.ReactNode;
  /** Props forwarded to the description element; the one live call site sets a className. */
  descriptionProps?: { className?: string };
  error?: React.ReactNode;
  withAsterisk?: boolean;
}

/** A native option. It keeps only the fields the current callers provide. */
export interface NativeSelectItem {
  value: string;
  label?: string;
  disabled?: boolean;
  group?: string;
}

/** Mantine 7's right-section props, with the values Mantine 6's Input behaved as. */
interface RightSectionProps {
  rightSectionPointerEvents: React.CSSProperties["pointerEvents"];
  rightSectionProps?: React.ComponentPropsWithoutRef<"div">;
}

const HIDDEN_SECTION: React.ComponentPropsWithoutRef<"div"> = { style: { display: "none" } };

/**
 * Mantine 6's Input right section took pointer events, and a wrapper holding a disabled `<input>` hid
 * it (`:has(input:disabled)`). Mantine 7 defaults the section to `pointer-events: none` and never hides
 * it, so every `<input>`-backed wrapper restores both (SC-2703).
 */
const mantine6RightSection = (disabled: boolean | undefined): RightSectionProps => ({
  rightSectionPointerEvents: "auto",
  rightSectionProps: disabled ? HIDDEN_SECTION : undefined,
});

/**
 * Mantine 6 marked an errored field's element `data-invalid`, which consumer CSS guards key on
 * (ReadWriteTextArea); Mantine 7 marks it `data-error`, so every field wrapper restates the Mantine 6
 * attribute (SC-2703).
 */
const invalidAttribute = (error: React.ReactNode): "true" | undefined => (error ? "true" : undefined);

export interface TextInputProps extends Omit<ElementProps<"input">, "size">, StyleProps, FieldBaseProps {
  icon?: React.ReactNode;
  rightSection?: React.ReactNode;
  rightSectionWidth?: number;
  variant?: "default" | "filled" | "unstyled";
  /** Non-numeric: Mantine resolves input size by theme key only, so a number would silently produce no size. */
  size?: Exclude<SizeValue, number>;
}

/**
 * Mantine 7 renamed `icon` to `leftSection`; the owned name is unchanged. Mantine 6 also defaulted
 * `type` to `"text"`, which the global `[data-sc-input][type="text"]` rule keys on (SC-2703).
 */
export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(function TextInput({ icon, type = "text", ...props }, ref) {
  return (
    <MantineTextInput
      {...props}
      {...mantine6RightSection(props.disabled)}
      type={type}
      leftSection={icon}
      data-invalid={invalidAttribute(props.error)}
      data-sc-input="true"
      ref={ref}
    />
  );
});

export interface NumberInputProps
  extends Omit<ElementProps<"input">, "size" | "onChange" | "value" | "defaultValue" | "type">, StyleProps, FieldBaseProps {
  value?: NumberInputValue;
  defaultValue?: NumberInputValue;
  onChange?: (value: NumberInputValue) => void;
  /** Decimal places kept while typing and shown on blur. */
  precision?: number;
  /** Only read when `precision` is set: drops trailing zeros on format. */
  removeTrailingZeros?: boolean;
  min?: number;
  max?: number;
  step?: number;
  hideControls?: boolean;
  icon?: React.ReactNode;
  rightSection?: React.ReactNode;
  rightSectionWidth?: number;
  /** Non-numeric: Mantine resolves input size by theme key only, so a number would silently produce no size. */
  size?: Exclude<SizeValue, number>;
  /**
   * Display formatting and its inverse, paired — `CurrencyInput` supplies `parseCurrency` and the
   * money formatters (both plain, tested functions in `~/utils/formatters` and
   * `~/modules/multi-currency/ui`). The wrapper calls both with the raw input text (never undefined),
   * as Mantine 6's NumberInput did.
   */
  formatter?: (value: string) => string;
  parser?: (value: string) => string;
}

/**
 * Mantine 7's NumberInput stylesheet classes. The owned NumberInput is not built on Mantine 7's
 * component (see below), but its step controls reuse these classes so they render as Mantine 7's do.
 */
const numberInputClasses = MantineNumberInput.classes;

/** The step-control chevron; Mantine 6 and Mantine 7 draw the same path. */
const CHEVRON_PATH = [
  "M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657",
  "11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803",
  "10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z",
].join(" ");

const CHEVRON_STYLE: React.CSSProperties = { width: "var(--ni-chevron-size)", height: "var(--ni-chevron-size)" };

const StepChevron = ({ direction }: { direction: "up" | "down" }): React.JSX.Element => (
  <svg style={direction === "up" ? { ...CHEVRON_STYLE, transform: "rotate(180deg)" } : CHEVRON_STYLE} viewBox="0 0 15 15" fill="none">
    <path d={CHEVRON_PATH} fill="currentColor" fillRule="evenodd" clipRule="evenodd" />
  </svg>
);

/**
 * Mantine 6's NumberInput, kept on Mantine 7's TextInput (SC-2703). Mantine 7 rebuilt NumberInput on
 * react-number-format: `formatter`/`parser` are gone, `precision` became `decimalScale`, it clamps on
 * blur instead of on every change, and its `onChange` can emit a string. Mapping onto it would change
 * what the money and quantity fields emit, so the wrapper keeps Mantine 6's state machine. The arithmetic
 * and the transitions — raw text while focused, the formatted value otherwise, and a re-sync from `value`
 * only when `value` or focus changes — are plain functions in `number-input-value.ts` (kind-1 tested);
 * the wiring below holds the state and applies them.
 */
export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  {
    value,
    defaultValue,
    onChange,
    precision = 0,
    removeTrailingZeros = false,
    min,
    max,
    step = 1,
    hideControls = false,
    icon,
    rightSection,
    rightSectionWidth,
    size,
    formatter = defaultNumberInputFormatter,
    parser = defaultNumberInputParser,
    className,
    disabled,
    readOnly,
    inputMode,
    onFocus,
    onBlur,
    onKeyDown,
    ...props
  },
  ref,
) {
  const format = (next: NumberInputValue): string => formatNumberInputValue(next, { precision, removeTrailingZeros, formatter });
  const [internalValue, setInternalValueState] = React.useState<NumberInputValue>(
    typeof value === "number" ? value : typeof defaultValue === "number" ? defaultValue : "",
  );
  const [inputValue, setInputValue] = React.useState(() => format(internalValue));
  const [focused, setFocused] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const mergedRef = useMergedRef(inputRef, ref);
  const os = useOs();
  const sizeKey = size ?? "sm";

  // Every transition is a plain function of this render's state (`number-input-value.ts`, kind-1 tested);
  // the component writes back only what changed and emits when the transition says so.
  const state: NumberInputState = { internalValue, inputValue };
  const commit = (next: NumberInputState): void => {
    if (next.inputValue !== inputValue) {
      setInputValue(next.inputValue);
    }
    if (next.internalValue !== internalValue) {
      setInternalValueState(next.internalValue);
    }
  };
  const apply = (transition: NumberInputTransition): void => {
    commit(transition.state);
    if (transition.emit) {
      onChange?.(transition.state.internalValue);
    }
  };

  // Mantine 6 re-synced from `value` only when `value` or focus changed, reading that render's state;
  // the ref hands the effect the current render's closure without widening its dependencies.
  const syncFromValue = React.useRef<() => void>(() => undefined);
  syncFromValue.current = () => commit(syncNumberInputState(state, value, { focused, format }));
  React.useEffect(() => {
    syncFromValue.current();
  }, [value, focused]);

  const stepBy = (event: React.SyntheticEvent, direction: 1 | -1): void => {
    event.preventDefault();
    inputRef.current?.focus();
    apply(stepNumberInputState(state, direction, { focused, format, step, precision, min, max }));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isComposingNumberInputChange(event.nativeEvent)) {
      return;
    }

    apply(changeNumberInputState(state, event.target.value, { focused, format, precision, min, max, parser }));
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
    setFocused(true);
    onFocus?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    setFocused(false);
    onBlur?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    onKeyDown?.(event);
    const direction = getNumberInputKeyStep(event.key, readOnly);
    if (direction !== undefined) {
      stepBy(event, direction);
    }
  };

  // Mantine 6 compared a cleared field against the bounds as 0.
  const comparableValue = internalValue === "" ? 0 : internalValue;
  const controls = (
    <MantineBox className={numberInputClasses.controls} style={{ "--ni-chevron-size": `var(--ni-chevron-size-${sizeKey})` }}>
      <MantineUnstyledButton
        className={numberInputClasses.control}
        tabIndex={-1}
        aria-hidden
        disabled={max !== undefined && comparableValue >= max}
        onPointerDown={(event: React.PointerEvent<HTMLButtonElement>) => stepBy(event, 1)}
      >
        <StepChevron direction="up" />
      </MantineUnstyledButton>
      <MantineUnstyledButton
        className={numberInputClasses.control}
        tabIndex={-1}
        aria-hidden
        disabled={min !== undefined && comparableValue <= min}
        onPointerDown={(event: React.PointerEvent<HTMLButtonElement>) => stepBy(event, -1)}
      >
        <StepChevron direction="down" />
      </MantineUnstyledButton>
    </MantineBox>
  );

  return (
    <MantineTextInput
      {...props}
      {...mantine6RightSection(disabled)}
      className={classNames(numberInputClasses.root, className)}
      type="text"
      value={inputValue}
      disabled={disabled}
      readOnly={readOnly}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      leftSection={icon}
      rightSection={rightSection || (disabled || readOnly || hideControls ? null : controls)}
      rightSectionWidth={rightSectionWidth ?? `var(--ni-right-section-width-${sizeKey})`}
      size={size}
      min={min}
      max={max}
      step={step}
      inputMode={inputMode || getNumberInputMode(step, precision, os)}
      data-invalid={invalidAttribute(props.error)}
      data-sc-input="true"
      ref={mergedRef}
    />
  );
});

export interface TextareaProps extends ElementProps<"textarea">, StyleProps, FieldBaseProps {
  variant?: "default" | "filled" | "unstyled";
  autosize?: boolean;
  minRows?: number;
  maxRows?: number;
  /** Non-numeric: Mantine resolves input size by theme key only, so a number would silently produce no size. */
  size?: Exclude<SizeValue, number>;
}

/**
 * Mantine 6 gave a non-autosize Textarea `rows={minRows}` (over any `rows` passed); Mantine 7 reads
 * `minRows` only while autosizing, so the wrapper sets the attribute itself (SC-2703).
 */
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(props, ref) {
  return (
    <MantineTextarea
      {...props}
      rows={props.autosize ? props.rows : props.minRows}
      data-invalid={invalidAttribute(props.error)}
      data-sc-input="true"
      ref={ref}
    />
  );
});

export interface InputProps extends Omit<ElementProps<"input">, "size">, StyleProps {
  icon?: React.ReactNode;
  rightSection?: React.ReactNode;
  variant?: "default" | "filled" | "unstyled";
  error?: React.ReactNode;
  /** Non-numeric: Mantine resolves input size by theme key only, so a number would silently produce no size. */
  size?: Exclude<SizeValue, number>;
}

/** The props the Input cast routes to Mantine 7: `icon` as its `leftSection`, plus the Mantine 6 right section. */
type InputRootProps = Omit<InputProps, "icon"> & RightSectionProps & { leftSection?: React.ReactNode };

/**
 * Same polymorphic flattening as `ButtonRoot` in the buttons module: Mantine types `Input` as a
 * polymorphic union whose `component` arm selects the element prop set, and a non-generic wrapper
 * cannot restate that. `component` is not declared because no call site renders it as anything but
 * its default `<input>` root. `inputs-casts.test.ts` pins the own-prop keys the cast routes to
 * Mantine; the intrinsic `<input>` props reach Mantine through the polymorphic rest-prop spread
 * and rest on the cast alone.
 */
const InputRoot = MantineInput as React.ComponentType<InputRootProps & { readonly ref?: React.Ref<HTMLInputElement> }>;

const InputBase = React.forwardRef<HTMLInputElement, InputProps>(function Input({ icon, ...props }, ref) {
  return (
    <InputRoot
      {...props}
      {...mantine6RightSection(props.disabled)}
      leftSection={icon}
      data-invalid={invalidAttribute(props.error)}
      data-sc-input="true"
      ref={ref}
    />
  );
});

export interface InputWrapperProps extends ElementProps<"div">, StyleProps, FieldBaseProps {
  children?: React.ReactNode;
  required?: boolean;
  /** Non-numeric: Mantine resolves the label/description size by theme key only. */
  size?: Exclude<SizeValue, number>;
}

const InputWrapper = React.forwardRef<HTMLDivElement, InputWrapperProps>(function InputWrapper({ children, ...props }, ref) {
  return (
    <MantineInput.Wrapper {...props} ref={ref}>
      {children}
    </MantineInput.Wrapper>
  );
});

export interface InputLabelProps extends ElementProps<"label">, StyleProps {
  children?: React.ReactNode;
  required?: boolean;
  size?: Exclude<SizeValue, number>;
}

const InputLabel = React.forwardRef<HTMLLabelElement, InputLabelProps>(function InputLabel({ children, ...props }, ref) {
  return (
    <MantineInput.Label {...props} ref={ref}>
      {children}
    </MantineInput.Label>
  );
});

export interface InputErrorProps extends ElementProps<"div">, StyleProps {
  children?: React.ReactNode;
  size?: Exclude<SizeValue, number>;
}

const InputError = React.forwardRef<HTMLDivElement, InputErrorProps>(function InputError({ children, ...props }, ref) {
  return (
    <MantineInput.Error {...props} ref={ref}>
      {children}
    </MantineInput.Error>
  );
});

export interface InputDescriptionProps extends ElementProps<"div">, StyleProps {
  children?: React.ReactNode;
  size?: Exclude<SizeValue, number>;
}

const InputDescription = React.forwardRef<HTMLDivElement, InputDescriptionProps>(function InputDescription({ children, ...props }, ref) {
  return (
    <MantineInput.Description {...props} ref={ref}>
      {children}
    </MantineInput.Description>
  );
});

export const Input = Object.assign(InputBase, {
  Wrapper: InputWrapper,
  Label: InputLabel,
  Error: InputError,
  Description: InputDescription,
});

export interface FileInputProps<Multiple extends boolean = false>
  extends Omit<ElementProps<"button">, "size" | "onChange" | "value" | "defaultValue">, StyleProps, FieldBaseProps {
  icon?: React.ReactNode;
  rightSection?: React.ReactNode;
  rightSectionWidth?: number;
  variant?: "default" | "filled" | "unstyled";
  size?: Exclude<SizeValue, number>;
  value?: Multiple extends true ? File[] : File | null;
  defaultValue?: Multiple extends true ? File[] : File | null;
  onChange?: (value: Multiple extends true ? File[] : File | null) => void;
  multiple?: Multiple;
  accept?: string;
  placeholder?: string;
  valueComponent?: React.FC<{ value: File | File[] | null }>;
  clearable?: boolean;
  clearButtonProps?: React.ComponentPropsWithoutRef<"button">;
  readOnly?: boolean;
  capture?: boolean | "user" | "environment";
  fileInputProps?: React.ComponentPropsWithoutRef<"input">;
  /** Owned class for the input slot. */
  inputClassName?: string;
  /** Owned class for the leading icon slot. */
  iconClassName?: string;
  /** Owned class for the placeholder slot. */
  placeholderClassName?: string;
}

/**
 * Mantine 7 merged the `icon` and `rightSection` slots into one `section` slot, so the owned icon class
 * reaches the left section through its own props rather than the class map (SC-2703). Mantine 6's
 * clear button sat in a right section that took pointer events; Mantine 7's defaults to none.
 */
export function FileInput<Multiple extends boolean = false>({
  icon,
  inputClassName,
  iconClassName,
  placeholderClassName,
  ...props
}: FileInputProps<Multiple>): React.JSX.Element {
  const slotClassNames = inputClassName || placeholderClassName ? { input: inputClassName, placeholder: placeholderClassName } : undefined;

  return (
    <MantineFileInput<Multiple>
      {...props}
      classNames={slotClassNames}
      leftSection={icon}
      leftSectionProps={iconClassName ? { className: iconClassName } : undefined}
      rightSectionPointerEvents="auto"
      data-invalid={invalidAttribute(props.error)}
      data-sc-input="true"
    />
  );
}

export interface NativeSelectProps extends Omit<ElementProps<"select">, "size">, StyleProps, FieldBaseProps {
  data: Array<string | NativeSelectItem>;
  icon?: React.ReactNode;
  rightSection?: React.ReactNode;
  rightSectionWidth?: number;
  variant?: "default" | "filled" | "unstyled";
  size?: Exclude<SizeValue, number>;
  placeholder?: string;
  /** Owned class for the select input slot. */
  inputClassName?: string;
  /** Owned class for the leading icon slot. */
  iconClassName?: string;
  /** Owned class for the placeholder slot. */
  placeholderClassName?: string;
}

/**
 * Mantine 6 rendered one `<option>` per item — a string is its own label, a missing label stays
 * empty, `group` is ignored. Mantine 7 parses `data` as Combobox data, which reads `group` as an
 * option group and fills a missing label with the value, so the wrapper renders the options itself.
 * Mantine 6 kept its chevron inert and a custom right section clickable; Mantine 7 makes both inert.
 * The icon class reaches the left section as in `FileInput`; the placeholder class has no element to
 * land on, as in Mantine 6 (SC-2703).
 */
export const NativeSelect = React.forwardRef<HTMLSelectElement, NativeSelectProps>(function NativeSelect(
  { data, icon, rightSection, inputClassName, iconClassName, placeholderClassName, ...props },
  ref,
) {
  const slotClassNames = inputClassName || placeholderClassName ? { input: inputClassName, placeholder: placeholderClassName } : undefined;

  return (
    <MantineNativeSelect
      {...props}
      classNames={slotClassNames}
      leftSection={icon}
      leftSectionProps={iconClassName ? { className: iconClassName } : undefined}
      rightSection={rightSection}
      rightSectionPointerEvents={rightSection ? "auto" : "none"}
      data-invalid={invalidAttribute(props.error)}
      data-sc-input="true"
      ref={ref}
    >
      {data.map(item => {
        const option: NativeSelectItem = typeof item === "string" ? { value: item, label: item } : item;
        return (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        );
      })}
    </MantineNativeSelect>
  );
});
